/**
 * Advanced PDF Viewer Component with Editing Capabilities
 * 
 * Displays multiple PDFs with zoom controls, page navigation, and editing tools.
 * Uses pdf-lib for PDF manipulation and canvas for rendering with drag-and-drop text editing.
 * 
 * @component
 * @example
 * return <PdfViewer pdfUrl={pdfUrl} fileName="document.pdf" themeMode="dark" />
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    Description as PdfIcon,
    Edit as EditIcon,
    TextFields as TextIcon,
    Brush as PenIcon,
    Save as SaveIcon,
    Delete as DeleteIcon,
    AutoFixHigh as EraserIcon,
    Close as CloseIcon,
} from "@mui/icons-material";
import { Box, Typography, IconButton, Button, Tooltip, Popover, Select, MenuItem } from "@mui/material";
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';

/**
 * Convert hex color string to RGB values (0-1 range for pdf-lib)
 * @param {string} hex - Hex color string (e.g., '#FF6B47' or 'FF6B47')
 * @returns {{r: number, g: number, b: number}} RGB values in 0-1 range
 */
const hexToRgb = (hex) => {
    // Remove # if present
    const cleanHex = hex.replace('#', '');
    const bigint = parseInt(cleanHex, 16);
    return {
        r: ((bigint >> 16) & 255) / 255,
        g: ((bigint >> 8) & 255) / 255,
        b: (bigint & 255) / 255
    };
};

/**
 * Predefined color palette for text and pen
 */
const COLOR_PALETTE = [
    '#000000', // Black
    '#FFFFFF', // White
    '#FF0000', // Red
    '#00FF00', // Green
    '#0000FF', // Blue
    '#FF6B47', // Orange (theme color)
    '#FFFF00', // Yellow
    '#FF00FF', // Magenta
    '#00FFFF', // Cyan
    '#800080', // Purple
    '#FFA500', // Orange
    '#808080', // Gray
];

/**
 * Color Picker Component
 */
const ColorPicker = ({ color, onChange, label, themeMode, anchorEl, onClose }) => {
    const open = Boolean(anchorEl);
    
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
            PaperProps={{
                sx: {
                    backgroundColor: themeMode === 'dark' ? '#252144' : '#ffffff',
                    border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e0e0e0'}`,
                    borderRadius: '8px',
                    padding: '12px',
                }
            }}
        >
            <Typography
                sx={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: themeMode === 'dark' ? '#8B8FA8' : '#666666',
                    marginBottom: '8px',
                }}
            >
                {label}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
                {COLOR_PALETTE.map((c) => (
                    <Box
                        key={c}
                        onClick={() => {
                            onChange(c);
                            onClose();
                        }}
                        sx={{
                            width: '24px',
                            height: '24px',
                            backgroundColor: c,
                            border: color === c 
                                ? '2px solid #FF6B47' 
                                : `1px solid ${c === '#FFFFFF' ? '#ccc' : 'transparent'}`,
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'transform 0.1s',
                            '&:hover': {
                                transform: 'scale(1.1)',
                            },
                        }}
                    />
                ))}
            </Box>
        </Popover>
    );
};

/**
 * Generates a stable unique identifier for a PDF file.
 * This ID persists across re-renders and array reordering.
 */
const generatePdfId = (file, type, index) => {
    if (file && file.name) {
        // Use file properties for stable ID
        return `${type}-${file.name}-${file.size || 0}-${file.lastModified || index}`;
    }
    // Fallback for URL-based PDFs (templates from modal)
    return `${type}-url-${index}-${Date.now()}`;
};

// Set up PDF.js worker
const setupPDFWorker = () => {
    // Use unpkg CDN which is more reliable and has the correct file structure
    // Using .mjs extension for ES modules (required for v5+)
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

    console.log('🔧 PDF.js Worker configured:', {
        version: pdfjsLib.version,
        workerSrc: pdfjsLib.GlobalWorkerOptions.workerSrc
    });
};

setupPDFWorker();

/**
 * Individual Page Canvas Component
 * Renders a single PDF page with annotations
 */
/**
 * Individual Page Canvas Component
 * Renders a single PDF page with annotations
 * CRITICAL FIX: Uses pdfId instead of pdfIndex for stable text association
 */
const PageCanvas = React.memo(({ 
    pageImage, 
    zoom, 
    themeMode, 
    isEditing, 
    editMode, 
    isDragging, 
    hoveredText,
    textElements,
    penStrokes,
    editingText,
    editingValue,
    selectedTextElement,
    onCanvasClick,
    onCanvasDoubleClick,
    onCanvasMouseDown,
    onCanvasMouseMove,
    onTextInputChange,
    onFinishTextEdit,
    inputRef,
    renderPenStrokes,
    renderTextElements
}) => {
    const canvasRef = useRef(null);
    
    useEffect(() => {
        if (pageImage && canvasRef.current) {
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            
            const img = new Image();
            img.onload = () => {
                // Use pageImage dimensions (original viewport size) instead of img dimensions
                // (which include the pixelRatio scaling used for high-res rendering)
                // Apply zoom multiplier: 100% zoom (zoom=1) should display at 75% scale
                const zoomMultiplier = zoom * 0.75;
                const viewport = {
                    width: pageImage.width * zoomMultiplier,
                    height: pageImage.height * zoomMultiplier
                };
                
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                
                context.drawImage(img, 0, 0, viewport.width, viewport.height);
                
                // Render pen strokes and text with pdfScale for correct coordinates
                const pdfScale = pageImage.pdfScale ?? 1;
                renderPenStrokes(context, pageImage.pdfId, pageImage.pageIndex, pdfScale, zoomMultiplier);
                renderTextElements(context, viewport, pageImage.pdfId, pageImage.pageIndex, pdfScale, zoomMultiplier);
            };
            img.src = pageImage.dataUrl;
        }
    }, [pageImage, zoom, textElements, penStrokes, selectedTextElement, editingText]);
    
    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <canvas
                ref={canvasRef}
                onClick={(e) => {
                    e.pageImage = pageImage;
                    onCanvasClick(e);
                }}
                onDoubleClick={(e) => {
                    e.pageImage = pageImage;
                    onCanvasDoubleClick(e);
                }}
                onMouseDown={(e) => {
                    e.pageImage = pageImage;
                    onCanvasMouseDown(e);
                }}
                onMouseMove={(e) => {
                    e.pageImage = pageImage;
                    onCanvasMouseMove(e);
                }}
                style={{
                    border: `2px solid ${isEditing ? "#FF6B47" : (themeMode === 'dark' ? "#302d4c" : "#e0e0e0")}`,
                    borderRadius: "8px",
                    backgroundColor: "white",
                    boxShadow: themeMode === 'dark' ? "0 4px 12px rgba(0,0,0,0.3)" : "0 4px 12px rgba(0,0,0,0.1)",
                    cursor: isEditing 
                        ? (editMode === 'pen' 
                            ? 'crosshair' 
                            : editMode === 'eraser'
                                ? 'cell'
                                : (isDragging 
                                    ? 'grabbing' 
                                    : (hoveredText 
                                        ? 'grab' 
                                        : 'text'
                                    )
                                )
                        ) 
                        : 'default',
                    display: 'block',
                }}
            />
            
            {/* Inline text input overlay for this specific page */}
            {/* CRITICAL FIX: Use pdfId instead of pdfIndex */}
            {editingText && (() => {
                const textElement = textElements.find(t => 
                    t.id === editingText && 
                    t.pdfId === pageImage.pdfId && 
                    t.pageIndex === pageImage.pageIndex
                );
                if (!textElement) return null;
                const pdfScale = pageImage.pdfScale ?? 1;
                const zoomMultiplier = zoom * 0.75; // Apply 0.75 multiplier for display
                const s = pdfScale * zoomMultiplier;
                return (
                    <input
                        ref={inputRef}
                        type="text"
                        value={editingValue}
                        onChange={onTextInputChange}
                        onBlur={() => {
                            onFinishTextEdit();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                onFinishTextEdit();
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                onFinishTextEdit();
                            }
                        }}
                        autoFocus
                        style={{
                            position: 'absolute',
                            left: `${textElement.x * s}px`,
                            top: `${(textElement.y - (textElement.fontSize || 16)) * s}px`,
                            fontSize: `${(textElement.fontSize || 16) * s}px`,
                            fontFamily: 'Arial',
                            border: '2px solid #FF6B47',
                            borderRadius: '4px',
                            padding: '2px 4px',
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            color: '#000000',
                            outline: 'none',
                            minWidth: '100px',
                            maxWidth: '500px',
                            zIndex: 1000,
                            pointerEvents: 'auto',
                            boxShadow: '0 2px 8px rgba(255, 107, 71, 0.3)',
                        }}
                    />
                );
            })()}
        </div>
    );
});

const PdfViewer = React.forwardRef(({
    pdfUrl,
    fileName,
    themeMode,
    coverPageFile = null,
    templateFile = null,
    medicalRecordsFiles = [],
    isFullscreen = false, // When true, auto-zoom to 200%; when false, reset to 100%
}, ref) => {
    const [currentPdfIndex, setCurrentPdfIndex] = useState(0);
    const [zoom, setZoom] = useState(1); // Start at 100% on first mount
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editMode, setEditMode] = useState('text'); // 'text', 'pen', 'eraser'
    
    // Auto-zoom when fullscreen changes: 200% in fullscreen, 100% when exiting
    useEffect(() => {
        if (isFullscreen) {
            setZoom(2); // 200%
        } else {
            setZoom(1); // 100%
        }
    }, [isFullscreen]);
    
    // Color state for text and pen
    const [textColor, setTextColor] = useState('#000000');
    const [penColor, setPenColor] = useState('#000000');
    
    // Font size and pen width state
    const [textFontSize, setTextFontSize] = useState(16);
    const [penWidth, setPenWidth] = useState(3);
    
    // Color picker popover anchors
    const [textColorAnchor, setTextColorAnchor] = useState(null);
    const [penColorAnchor, setPenColorAnchor] = useState(null);
    
    // Select dropdown open state for tooltip handling
    const [fontSizeSelectOpen, setFontSizeSelectOpen] = useState(false);
    const [penThicknessSelectOpen, setPenThicknessSelectOpen] = useState(false);
    
    // PDF documents and pages - now keyed by stable pdfId
    const [pdfDocuments, setPdfDocuments] = useState({}); // { [pdfId]: pdfDoc }
    const [pageImages, setPageImages] = useState([]);
    
    // Editing state - text elements now use pdfId instead of pdfIndex
    // Structure: { id, text, x, y, fontSize, color, pdfId, pageIndex, textId }
    const [textElements, setTextElements] = useState([]);
    const [selectedTextElement, setSelectedTextElement] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [editingText, setEditingText] = useState(null); // For inline text editing
    const [editingValue, setEditingValue] = useState('');
    const [mouseDownPos, setMouseDownPos] = useState(null); // Track mouse down position
    const [hasMoved, setHasMoved] = useState(false); // Track if mouse moved during drag
    
    // Pen drawing state - pen strokes now use pdfId instead of pdfIndex
    const [isDrawing, setIsDrawing] = useState(false);
    const [penStrokes, setPenStrokes] = useState([]);
    const [currentStroke, setCurrentStroke] = useState([]);
    const [activeCanvas, setActiveCanvas] = useState(null); // Track active canvas during drawing/dragging
    
    // Track previous PDF IDs for cleanup
    const prevPdfIdsRef = useRef(new Set());
    
    // Track URLs for cleanup
    const urlsRef = useRef([]);
    
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const [hoveredText, setHoveredText] = useState(null); // Track hovered text

    // Zoom levels: 33% normal view, 70% edit mode; 0.5, 0.75, … to zoom in/out
    const zoomLevels = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
    const currentZoomIndex = zoomLevels.indexOf(zoom);

    /**
     * CRITICAL FIX: Memoize allPdfs with stable IDs
     * Each PDF gets a unique, stable pdfId that persists across re-renders
     * This prevents text from jumping to wrong PDFs when order changes
     */
    const allPdfs = useMemo(() => {
        // Clean up old URLs first
        urlsRef.current.forEach(url => {
            try { URL.revokeObjectURL(url); } catch (e) {}
        });
        urlsRef.current = [];
        
        const pdfs = [];
        let displayIndex = 0;
        
        // 1. Cover Letter
        if (coverPageFile) {
            const url = URL.createObjectURL(coverPageFile);
            urlsRef.current.push(url);
            pdfs.push({
                url,
                name: coverPageFile.name,
                type: 'cover',
                file: coverPageFile,
                pdfId: generatePdfId(coverPageFile, 'cover', 0),
                displayIndex: displayIndex++
            });
        }
        
        // 2. Template (either from modal selection via pdfUrl, or manual upload via templateFile)
        // FIX: Prioritize templateFile over pdfUrl to prevent duplicates when both are set
        if (templateFile) {
            // If templateFile exists, use it (this is the actual File object needed for sending)
            const url = URL.createObjectURL(templateFile);
            urlsRef.current.push(url);
            pdfs.push({
                url,
                name: templateFile.name,
                type: 'template',
                file: templateFile,
                pdfId: generatePdfId(templateFile, 'template', 1),
                displayIndex: displayIndex++
            });
        } else if (pdfUrl) {
            // Only use pdfUrl if templateFile is not present (fallback for URL-only templates)
            pdfs.push({
                url: pdfUrl,
                name: fileName || 'Template',
                type: 'template',
                file: null,
                pdfId: `template-url-${fileName || 'default'}`,
                displayIndex: displayIndex++
            });
        }
        
        // 3. Medical Records
        medicalRecordsFiles.forEach((file, idx) => {
            const url = URL.createObjectURL(file);
            urlsRef.current.push(url);
            pdfs.push({
                url,
                name: file.name,
                type: 'medical',
                file,
                pdfId: generatePdfId(file, 'medical', idx),
                displayIndex: displayIndex++
            });
        });
        
        return pdfs;
    }, [coverPageFile, templateFile, medicalRecordsFiles, pdfUrl, fileName]);
    
    /**
     * CRITICAL FIX: Clean up text elements and pen strokes when PDFs are removed
     * This ensures Rule 3: "Deleting a PDF deletes its text"
     */
    useEffect(() => {
        const currentPdfIds = new Set(allPdfs.map(pdf => pdf.pdfId));
        const prevPdfIds = prevPdfIdsRef.current;
        
        // Find removed PDF IDs
        const removedPdfIds = [];
        prevPdfIds.forEach(pdfId => {
            if (!currentPdfIds.has(pdfId)) {
                removedPdfIds.push(pdfId);
            }
        });
        
        // Clean up text elements and pen strokes for removed PDFs
        if (removedPdfIds.length > 0) {
            console.log('🧹 Cleaning up annotations for removed PDFs:', removedPdfIds);
            
            setTextElements(prev => {
                const filtered = prev.filter(text => !removedPdfIds.includes(text.pdfId));
                console.log(`   Removed ${prev.length - filtered.length} text elements`);
                return filtered;
            });
            
            setPenStrokes(prev => {
                const filtered = prev.filter(stroke => !removedPdfIds.includes(stroke.pdfId));
                console.log(`   Removed ${prev.length - filtered.length} pen strokes`);
                return filtered;
            });
            
            // Clear selection if selected element was on removed PDF
            setSelectedTextElement(prev => {
                const selectedText = textElements.find(t => t.id === prev);
                if (selectedText && removedPdfIds.includes(selectedText.pdfId)) {
                    return null;
                }
                return prev;
            });
            
            setEditingText(null);
            setEditingValue('');
        }
        
        // Update previous PDF IDs ref
        prevPdfIdsRef.current = currentPdfIds;
    }, [allPdfs]);
    
    // Cleanup URLs on unmount
    useEffect(() => {
        return () => {
            urlsRef.current.forEach(url => {
                try { URL.revokeObjectURL(url); } catch (e) {}
            });
        };
    }, []);

    /**
     * Load all PDFs with individual error handling
     * CRITICAL FIX: Uses stable pdfId to track documents
     */
    useEffect(() => {
        if (allPdfs.length === 0) {
            setLoading(false);
            return;
        }

        const loadAllPdfs = async () => {
            setLoading(true);
            setError(null);
            const loadedDocs = {}; // Now keyed by pdfId
            const loadedPdfjsDocs = {};
            const allPageImages = [];
            const failedPdfs = [];
            const pageCountByPdfId = {}; // From PDF.js (avoids pdf-lib getPageCount on possibly broken docs)

            for (let displayIndex = 0; displayIndex < allPdfs.length; displayIndex++) {
                const pdfData = allPdfs[displayIndex];
                const { pdfId } = pdfData;
                
                console.log(`🔄 Loading PDF ${displayIndex + 1}/${allPdfs.length}:`, pdfData.name, `(ID: ${pdfId})`);

                try {
                    // Load PDF document with pdf-lib for editing
                    const response = await fetch(pdfData.url);
                    if (!response.ok) {
                        throw new Error(`Failed to fetch PDF: ${response.statusText}`);
                    }
                    
                    const pdfBytes = await response.arrayBuffer();
                    const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
                    loadedDocs[pdfId] = pdfDoc;
                    
                    // Load PDF with PDF.js for rendering (more lenient than pdf-lib for encrypted/quirky PDFs)
                    const pdfjsDoc = await pdfjsLib.getDocument(pdfData.url).promise;
                    loadedPdfjsDocs[pdfId] = pdfjsDoc;
                    pageCountByPdfId[pdfId] = pdfjsDoc.numPages;
                    
                    // Generate page images using PDF.js
                    const pageCount = pdfjsDoc.numPages;
                    
                    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
                        try {
                            const page = await pdfjsDoc.getPage(pageIndex + 1);
                            // Higher scale (1.5) + pixelRatio 2 for sharper PDF clarity
                            const scale = 1.5;
                            const pixelRatio = 2;
                            const viewport = page.getViewport({ scale });
                            
                            // Create canvas for this page
                            const canvas = document.createElement('canvas');
                            const context = canvas.getContext('2d');
                            
                            // Set canvas size at higher resolution for quality
                            canvas.width = viewport.width * pixelRatio;
                            canvas.height = viewport.height * pixelRatio;
                            canvas.style.width = viewport.width + 'px';
                            canvas.style.height = viewport.height + 'px';
                            context.scale(pixelRatio, pixelRatio);
                            
                            // Render the actual PDF page
                            const renderContext = {
                                canvasContext: context,
                                viewport: viewport
                            };
                            
                            await page.render(renderContext).promise;
                            
                            // pdfScale: convert between canvas pixels and PDF points (scale * pixelRatio)
                            const pdfScale = scale * pixelRatio;
                            // CRITICAL: Use pdfId instead of pdfIndex for stable association
                            allPageImages.push({
                                canvas: canvas,
                                dataUrl: canvas.toDataURL(),
                                width: viewport.width,
                                height: viewport.height,
                                pdfId: pdfId, // Stable identifier
                                pdfIndex: displayIndex, // Keep for display ordering
                                pageIndex: pageIndex,
                                pdfName: pdfData.name,
                                pdfType: pdfData.type,
                                pdfScale, // For coordinate conversion in handlers and render
                            });
                        } catch (pageError) {
                            console.error(`❌ Error loading page ${pageIndex + 1} of ${pdfData.name}:`, pageError);
                            // Continue with other pages
                        }
                    }
                    
                    console.log(`✅ Successfully loaded: ${pdfData.name} (${pdfjsDoc.numPages} pages, ID: ${pdfId})`);
                    
                } catch (pdfError) {
                    console.error(`❌ Error loading PDF ${pdfData.name}:`, pdfError);
                    failedPdfs.push({ name: pdfData.name, error: pdfError.message, pdfId });
                    // Continue with next PDF - don't break the loop
                }
            }

            // Update state with whatever we successfully loaded
            const loadedPdfIds = Object.keys(loadedDocs);
            if (loadedPdfIds.length > 0) {
                setPdfDocuments(loadedDocs);
                setPageImages(allPageImages);

                console.log('✅ PDFs loaded:', {
                    successful: loadedPdfIds.length,
                    failed: failedPdfs.length,
                    totalPages: allPageImages.length,
                    pdfIds: loadedPdfIds
                });

                // Show warning if some PDFs failed
                if (failedPdfs.length > 0) {
                    const failedNames = failedPdfs.map(f => f.name).join(', ');
                    setError(`Some PDFs failed to load: ${failedNames}`);
                }
            } else {
                setError('Failed to load any PDF files');
            }

            setLoading(false);
        };

        loadAllPdfs();
    }, [allPdfs]);

    /**
     * Render pen strokes on canvas for a specific page
     * pdfScale: scale * pixelRatio from PDF render for correct PDF↔canvas coordinate conversion
     */
    const renderPenStrokes = (context, pdfId, pageIndex, pdfScale = 1, displayZoom = zoom) => {
        const s = pdfScale * displayZoom;
        const currentPageStrokes = penStrokes.filter(stroke => 
            stroke.pdfId === pdfId && stroke.pageIndex === pageIndex
        );
        
        currentPageStrokes.forEach(stroke => {
            if (stroke.points.length < 2) return;
            
            context.strokeStyle = stroke.color || '#000000'; // Black pen
            context.lineWidth = (stroke.width || 3) * displayZoom; // Bolder pen
            context.lineCap = 'round';
            context.lineJoin = 'round';
            
            context.beginPath();
            context.moveTo(stroke.points[0].x * s, stroke.points[0].y * s);
            
            for (let i = 1; i < stroke.points.length; i++) {
                context.lineTo(stroke.points[i].x * s, stroke.points[i].y * s);
            }
            
            context.stroke();
        });
    };

    /**
     * Render text elements on canvas for a specific page
     * pdfScale: scale * pixelRatio from PDF render for correct PDF↔canvas coordinate conversion
     */
    const renderTextElements = (context, viewport, pdfId, pageIndex, pdfScale = 1, displayZoom = zoom) => {
        const s = pdfScale * displayZoom;
        const currentPageTexts = textElements.filter(text => 
            text.pdfId === pdfId && text.pageIndex === pageIndex
        );
        
        currentPageTexts.forEach(textElement => {
            // Always render text (even when editing - the input overlay will appear on top)
            context.fillStyle = textElement.color || '#000000'; // Black text
            context.font = `${(textElement.fontSize || 16) * s}px Arial`;
            context.textAlign = 'left';
            
            // Only render if text exists
            if (textElement.text && textElement.text.trim()) {
                context.fillText(
                    textElement.text,
                    textElement.x * s,
                    textElement.y * s
                );
            }
            
            // Draw selection border if selected (and not currently editing)
            if (selectedTextElement === textElement.id && !editingText) {
                context.strokeStyle = '#FF6B47';
                context.lineWidth = 2;
                const textWidth = context.measureText(textElement.text || 'A').width;
                context.strokeRect(
                    textElement.x * s - 2,
                    textElement.y * s - (textElement.fontSize || 16) * s,
                    Math.max(textWidth, 20) + 4,
                    (textElement.fontSize || 16) * s + 4
                );
            }
        });
    };

    /**
     * Handle canvas click for selecting text or other actions
     * Only fires if it's a true click (not a drag)
     * CRITICAL FIX: Uses pdfId instead of pdfIndex for stable text association
     */
    const handleCanvasClick = (event) => {
        // Don't process if it was a drag operation
        if (hasMoved) {
            setHasMoved(false);
            return;
        }
        
        if (!isEditing) return;
        if (editMode !== 'text') return;

        const canvas = event.currentTarget;
        const rect = canvas.getBoundingClientRect();
        const pageImage = event.pageImage;
        if (!pageImage) return;
        const pdfScale = pageImage.pdfScale ?? 1;
        const zoomMultiplier = zoom * 0.75; // Apply 0.75 multiplier for display
        const x = (event.clientX - rect.left) / (pdfScale * zoomMultiplier);
        const y = (event.clientY - rect.top) / (pdfScale * zoomMultiplier);

        // CRITICAL FIX: Use pdfId instead of pdfIndex
        // Check if clicked on existing text element
        const currentPageTexts = textElements.filter(text => 
            text.pdfId === pageImage.pdfId && text.pageIndex === pageImage.pageIndex
        );
        
        let clickedOnText = false;
        for (const textElement of currentPageTexts) {
            const fontSize = textElement.fontSize || 16;
            const context = canvas.getContext('2d');
            context.font = `${fontSize}px Arial`;
            const textWidth = context.measureText(textElement.text).width;
            
            // Check if click is within text bounds
            if (
                x >= textElement.x &&
                x <= textElement.x + textWidth &&
                y >= textElement.y - fontSize &&
                y <= textElement.y
            ) {
                clickedOnText = true;
                
                // If already selected, open for editing
                if (selectedTextElement === textElement.id) {
                    setEditingText(textElement.id);
                    setEditingValue(textElement.text);
                    setCurrentPdfIndex(pageImage.pdfIndex);
                    
                    // Focus the input after a short delay
                    setTimeout(() => {
                        if (inputRef.current) {
                            inputRef.current.focus();
                            inputRef.current.select();
                        }
                    }, 50);
                } else {
                    // Just select it (for dragging)
                    setSelectedTextElement(textElement.id);
                    setCurrentPdfIndex(pageImage.pdfIndex);
                }
                return;
            }
        }
        
        // If no text was clicked, deselect
        if (!clickedOnText) {
            setSelectedTextElement(null);
            setEditingText(null);
        }
    };

    /**
     * Handle canvas double-click for adding text
     * CRITICAL FIX: Uses pdfId instead of pdfIndex for stable text association
     * Rule 1: Text must be bound to a specific PDF page using pdfId + pageIndex
     */
    const handleCanvasDoubleClick = (event) => {
        if (!isEditing || editMode !== 'text') return;

        const canvas = event.currentTarget;
        const rect = canvas.getBoundingClientRect();
        const pageImage = event.pageImage;
        if (!pageImage) return;
        const pdfScale = pageImage.pdfScale ?? 1;
        const zoomMultiplier = zoom * 0.75; // Apply 0.75 multiplier for display
        const x = (event.clientX - rect.left) / (pdfScale * zoomMultiplier);
        const y = (event.clientY - rect.top) / (pdfScale * zoomMultiplier);

        // CRITICAL FIX: Use pdfId instead of pdfIndex
        // Check if double-clicking on existing text
        const currentPageTexts = textElements.filter(text => 
            text.pdfId === pageImage.pdfId && text.pageIndex === pageImage.pageIndex
        );
        
        for (const textElement of currentPageTexts) {
            const fontSize = textElement.fontSize || 16;
            const context = canvas.getContext('2d');
            context.font = `${fontSize}px Arial`;
            const textWidth = context.measureText(textElement.text).width;
            
            // If double-clicking on existing text, just open editor (don't create new)
            if (
                x >= textElement.x &&
                x <= textElement.x + textWidth &&
                y >= textElement.y - fontSize &&
                y <= textElement.y
            ) {
                setSelectedTextElement(textElement.id);
                setEditingText(textElement.id);
                setEditingValue(textElement.text);
                setCurrentPdfIndex(pageImage.pdfIndex);
                
                setTimeout(() => {
                    if (inputRef.current) {
                        inputRef.current.focus();
                        inputRef.current.select();
                    }
                }, 50);
                return;
            }
        }

        // CRITICAL FIX: Add new text with pdfId (stable identifier) instead of pdfIndex
        // This ensures Rule 1: Text must be bound to a specific PDF page
        // Uses a unique textId to track this specific text element
        const newTextElement = {
            id: Date.now(),
            textId: `text-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Unique text identifier
            text: 'Text',
            x: x,
            y: y,
            fontSize: textFontSize, // Use selected font size
            color: textColor, // Use selected text color
            pdfId: pageImage.pdfId, // STABLE identifier - text stays with this PDF
            pageIndex: pageImage.pageIndex
        };
        
        setTextElements(prev => [...prev, newTextElement]);
        setSelectedTextElement(newTextElement.id);
        setEditingText(newTextElement.id);
        setEditingValue('Text');
        setCurrentPdfIndex(pageImage.pdfIndex);
        
        // Focus the input after a short delay
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
                inputRef.current.select();
            }
        }, 50);
    };

    /**
     * Handle canvas mouse down for dragging or pen drawing
     * CRITICAL FIX: Uses pdfId instead of pdfIndex for stable text association
     */
    const handleCanvasMouseDown = (event) => {
        if (!isEditing) return;

        const canvas = event.currentTarget; // Use event target instead of ref
        const rect = canvas.getBoundingClientRect();
        const pageImage = event.pageImage;
        if (!pageImage) return;
        const pdfScale = pageImage.pdfScale ?? 1;
        const zoomMultiplier = zoom * 0.75; // Apply 0.75 multiplier for display
        const x = (event.clientX - rect.left) / (pdfScale * zoomMultiplier);
        const y = (event.clientY - rect.top) / (pdfScale * zoomMultiplier);

        // Store the active canvas for later use in mouse move
        setActiveCanvas(canvas);

        // Store mouse down position, pdfId, and pdfScale for reference
        setMouseDownPos({ x, y, pdfId: pageImage.pdfId, pageIndex: pageImage.pageIndex, pdfScale });
        setHasMoved(false);

        // Pen drawing mode
        if (editMode === 'pen') {
            setIsDrawing(true);
            setCurrentStroke([{ x, y }]);
            setCurrentPdfIndex(pageImage.pdfIndex);
            return;
        }

        // Eraser mode - erase pen strokes that are near the click point
        if (editMode === 'eraser') {
            const eraserRadiusPx = 15; // Radius in screen pixels
            const eraserRadius = eraserRadiusPx / (pdfScale * zoomMultiplier); // convert to PDF space
            const pdfId = pageImage.pdfId;
            const pageIndex = pageImage.pageIndex;
            
            // Find and remove strokes that have points near the click position
            setPenStrokes(prev => prev.filter(stroke => {
                // Only check strokes on the current page
                if (stroke.pdfId !== pdfId || stroke.pageIndex !== pageIndex) {
                    return true; // Keep strokes from other pages
                }
                
                // Check if any point in the stroke is within eraser radius (PDF space)
                const isNearClick = stroke.points.some(point => {
                    const distance = Math.sqrt(
                        Math.pow(point.x - x, 2) + Math.pow(point.y - y, 2)
                    );
                    return distance < eraserRadius;
                });
                
                return !isNearClick; // Remove if near click, keep otherwise
            }));
            
            // Start eraser dragging mode
            setIsDrawing(true);
            setCurrentPdfIndex(pageImage.pdfIndex);
            return;
        }

        // Text mode - check if clicking on text for dragging
        // CRITICAL FIX: Use pdfId instead of pdfIndex
        if (editMode === 'text') {
            const currentPageTexts = textElements.filter(text => 
                text.pdfId === pageImage.pdfId && text.pageIndex === pageImage.pageIndex
            );
            
            // Check if mouse down on any text element
            for (const textElement of currentPageTexts) {
                const fontSize = textElement.fontSize || 16;
                const context = canvas.getContext('2d');
                context.font = `${fontSize}px Arial`;
                const textWidth = context.measureText(textElement.text).width;
                
                if (
                    x >= textElement.x &&
                    x <= textElement.x + textWidth &&
                    y >= textElement.y - fontSize &&
                    y <= textElement.y
                ) {
                    // Mouse down on text - prepare for potential drag
                    setSelectedTextElement(textElement.id);
                    setCurrentPdfIndex(pageImage.pdfIndex);
                    setDragOffset({
                        x: x - textElement.x,
                        y: y - textElement.y
                    });
                    return;
                }
            }
        }
    };

    /**
     * Handle canvas hover for cursor changes (on individual canvas)
     * CRITICAL FIX: Uses pdfId instead of pdfIndex for stable text association
     */
    const handleCanvasHover = (event) => {
        // Only check hover when not dragging/drawing
        if (isDragging || isDrawing || !isEditing || editMode !== 'text' || editingText) {
            setHoveredText(null);
            return;
        }

        const canvas = event.currentTarget;
        const rect = canvas.getBoundingClientRect();
        const pageImage = event.pageImage;
        if (!pageImage) return;
        const pdfScale = pageImage.pdfScale ?? 1;
        const zoomMultiplier = zoom * 0.75; // Apply 0.75 multiplier for display
        const x = (event.clientX - rect.left) / (pdfScale * zoomMultiplier);
        const y = (event.clientY - rect.top) / (pdfScale * zoomMultiplier);

        // CRITICAL FIX: Use pdfId instead of pdfIndex
        // Check if hovering over text on this specific page
        const currentPageTexts = textElements.filter(text => 
            text.pdfId === pageImage.pdfId && text.pageIndex === pageImage.pageIndex
        );
        
        let foundHoveredText = null;
        const context = canvas.getContext('2d');
        
        for (const textElement of currentPageTexts) {
            const fontSize = textElement.fontSize || 16;
            context.font = `${fontSize}px Arial`;
            const textWidth = context.measureText(textElement.text || 'A').width;
            
            // Check if hovering over text
            if (
                x >= textElement.x &&
                x <= textElement.x + textWidth &&
                y >= textElement.y - fontSize &&
                y <= textElement.y
            ) {
                foundHoveredText = textElement.id;
                break;
            }
        }
        
        setHoveredText(foundHoveredText);
    };

    /**
     * Handle canvas mouse move for dragging or pen drawing (document level)
     * CRITICAL FIX: Rule 5 - Dragging text must NOT create duplicates
     * Updates position on the SAME text object, old position is overwritten
     */
    const handleCanvasMouseMove = useCallback((event) => {
        if (!activeCanvas) return;

        const canvas = activeCanvas;
        const rect = canvas.getBoundingClientRect();
        const pdfScale = (mouseDownPos && mouseDownPos.pdfScale) ?? 1;
        const zoomMultiplier = zoom * 0.75; // Apply 0.75 multiplier for display
        const x = (event.clientX - rect.left) / (pdfScale * zoomMultiplier);
        const y = (event.clientY - rect.top) / (pdfScale * zoomMultiplier);
        const s = pdfScale * zoomMultiplier;

        // Pen drawing mode
        if (isDrawing && editMode === 'pen') {
            setCurrentStroke(prev => [...prev, { x, y }]);
            
            // Draw on canvas in real-time for immediate feedback (coords in PDF space)
            const context = canvas.getContext('2d');
            if (currentStroke.length > 0) {
                context.strokeStyle = penColor; // Use selected pen color
                const zoomMultiplier = zoom * 0.75; // Apply 0.75 multiplier for display
                context.lineWidth = penWidth * zoomMultiplier;
                context.lineCap = 'round';
                context.lineJoin = 'round';
                
                const lastPoint = currentStroke[currentStroke.length - 1];
                context.beginPath();
                context.moveTo(lastPoint.x * s, lastPoint.y * s);
                context.lineTo(x * s, y * s);
                context.stroke();
            }
            return;
        }

        // Eraser mode - continuously erase strokes while dragging
        if (isDrawing && editMode === 'eraser' && mouseDownPos) {
            const eraserRadiusPx = 15;
            const zoomMultiplier = zoom * 0.75; // Apply 0.75 multiplier for display
            const eraserRadius = eraserRadiusPx / (pdfScale * zoomMultiplier); // PDF space
            const pdfId = mouseDownPos.pdfId;
            const pageIndex = mouseDownPos.pageIndex;
            
            // Find and remove strokes that have points near the current position
            setPenStrokes(prev => prev.filter(stroke => {
                // Only check strokes on the current page
                if (stroke.pdfId !== pdfId || stroke.pageIndex !== pageIndex) {
                    return true; // Keep strokes from other pages
                }
                
                // Check if any point in the stroke is within eraser radius (PDF space)
                const isNearClick = stroke.points.some(point => {
                    const distance = Math.sqrt(
                        Math.pow(point.x - x, 2) + Math.pow(point.y - y, 2)
                    );
                    return distance < eraserRadius;
                });
                
                return !isNearClick; // Remove if near click, keep otherwise
            }));
            return;
        }

        // Text dragging mode
        // CRITICAL FIX: Rule 5 - Dragging should update position on the SAME text object
        // NOT create a new duplicate. Old position is overwritten.
        if (mouseDownPos && selectedTextElement && editMode === 'text') {
            const distance = Math.sqrt(
                Math.pow(x - mouseDownPos.x, 2) + Math.pow(y - mouseDownPos.y, 2)
            );
            
            // Start dragging if moved more than 3 pixels
            if (distance > 3 && !isDragging) {
                setIsDragging(true);
                setHasMoved(true);
            }
            
            if (isDragging) {
                // CRITICAL: Update the SAME text element's position (no duplication)
                setTextElements(prev => prev.map(text => 
                    text.id === selectedTextElement 
                        ? { ...text, x: x - dragOffset.x, y: y - dragOffset.y }
                        : text
                ));
            }
        }
    }, [activeCanvas, isDragging, isDrawing, selectedTextElement, dragOffset, zoom, editMode, currentStroke, mouseDownPos, penColor]);

    /**
     * Handle canvas mouse up
     * CRITICAL FIX: Uses pdfId instead of pdfIndex for pen strokes
     */
    const handleCanvasMouseUp = () => {
        // Complete pen drawing with pdfId
        if (isDrawing && currentStroke.length > 1 && mouseDownPos) {
            // CRITICAL FIX: Use pdfId from mouseDownPos instead of currentPdfIndex
            const newStroke = {
                id: Date.now(),
                strokeId: `stroke-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                points: currentStroke,
                color: penColor, // Use selected pen color
                width: penWidth, // Use selected pen width
                pdfId: mouseDownPos.pdfId, // STABLE identifier
                pageIndex: mouseDownPos.pageIndex
            };
            setPenStrokes(prev => [...prev, newStroke]);
            setCurrentStroke([]);
        }
        
        setIsDrawing(false);
        setIsDragging(false);
        setMouseDownPos(null);
        setActiveCanvas(null); // Clear active canvas
    };

    // Add document-level mouse event listeners when dragging/drawing or mouse is down
    useEffect(() => {
        if (isDrawing || isDragging || (mouseDownPos && selectedTextElement)) {
            document.addEventListener('mousemove', handleCanvasMouseMove);
            document.addEventListener('mouseup', handleCanvasMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleCanvasMouseMove);
                document.removeEventListener('mouseup', handleCanvasMouseUp);
            };
        }
    }, [isDrawing, isDragging, mouseDownPos, selectedTextElement, handleCanvasMouseMove]);

    /**
     * Handle zoom change
     */
    const handleZoomIn = () => {
        if (currentZoomIndex < zoomLevels.length - 1) {
            setZoom(zoomLevels[currentZoomIndex + 1]);
        }
    };

    const handleZoomOut = () => {
        if (currentZoomIndex > 0) {
            setZoom(zoomLevels[currentZoomIndex - 1]);
        }
    };

    const handleResetZoom = () => {
        if (isFullscreen) {
            setZoom(2); // 200% in fullscreen
        } else {
            setZoom(1); // 100%
        }
    };

    /**
     * Handle editing mode toggle
     * Entering edit: zoom 70%; exiting: zoom 33%.
     */
    const handleEditToggle = () => {
        if (isEditing) {
            setIsEditing(false);
            setZoom(1); // Back to normal view zoom
        } else {
            setIsEditing(true);
            setEditMode('text');
            setZoom(1); // Edit mode: 70%
        }
    };

    /**
     * Handle edit mode change
     */
    const handleEditModeChange = (mode) => {
        setEditMode(mode);
    };

    /**
     * Handle delete selected text
     */
    const handleDeleteText = () => {
        if (selectedTextElement) {
            setTextElements(prev => prev.filter(text => text.id !== selectedTextElement));
            setSelectedTextElement(null);
            setEditingText(null);
            setEditingValue('');
            
            // Auto-save after deleting
            setTimeout(() => autoSave(), 500);
        }
    };

    /**
     * Handle text input change
     */
    const handleTextInputChange = (e) => {
        const newValue = e.target.value;
        setEditingValue(newValue);
        
        // Update text element in real-time
        if (editingText) {
            setTextElements(prev => prev.map(text => 
                text.id === editingText 
                    ? { ...text, text: newValue }
                    : text
            ));
        }
    };

    /**
     * Handle finishing text edit
     */
    const handleFinishTextEdit = (keepSelection = false) => {
        if (editingText) {
            // Only delete if text is completely empty
            if (!editingValue || editingValue.trim() === '') {
                setTextElements(prev => prev.filter(text => text.id !== editingText));
                setSelectedTextElement(null);
            } else if (!keepSelection) {
                // Keep text selected after editing unless explicitly told not to
                // This allows for dragging after editing
            }
            
            setEditingText(null);
            setEditingValue('');
            
            // Auto-save after editing
            setTimeout(() => autoSave(), 500);
        }
    };

    /**
     * Auto-save functionality
     * Changes are automatically saved in React state and will be included
     * when sending fax or downloading PDF via getEditedPdfs()
     * No need to manually save - all edits are preserved automatically
     */
    const autoSave = useCallback(() => {
        // Changes are automatically saved in React state
        // They will be included when exporting PDFs via getEditedPdfs()
        // This ensures all edits are preserved without manual save
        console.log('📝 Changes saved automatically');
    }, []);

    /**
     * Handle download edited PDFs (downloads all PDFs with edits)
     * Matches client-ui behavior - downloads all edited PDFs
     */
    const handleSavePdf = async () => {
        try {
            // Use the same export logic as getEditedPdfs
            const editedPdfs = await exportEditedPdfsForDownload();
            
            // Download all edited PDFs
            const filesToDownload = [];
            if (editedPdfs.coverPage) filesToDownload.push(editedPdfs.coverPage);
            if (editedPdfs.template) filesToDownload.push(editedPdfs.template);
            if (editedPdfs.medicalRecords && editedPdfs.medicalRecords.length > 0) {
                filesToDownload.push(...editedPdfs.medicalRecords);
            }
            
            filesToDownload.forEach((file) => {
                const url = URL.createObjectURL(file);
                const link = document.createElement('a');
                link.href = url;
                link.download = file.name;
                link.click();
                URL.revokeObjectURL(url);
            });
        } catch (error) {
            console.error('Error downloading PDFs:', error);
        }
    };
    
    /**
     * Helper function to export edited PDFs for download
     * Returns object with coverPage, template, and medicalRecords
     * This is the same logic used by getEditedPdfs
     */
    const exportEditedPdfsForDownload = async () => {
        try {
            console.log('🔄 Exporting edited PDFs for download...');
            console.log('   Text elements:', textElements.length);
            console.log('   Pen strokes:', penStrokes.length);
            
            const editedPdfs = {
                coverPage: null,
                template: null,
                medicalRecords: []
            };

            // Process each PDF using its stable pdfId
            for (const pdfInfo of allPdfs) {
                const { pdfId, type, name, url, file: originalFile } = pdfInfo;
                const pdfDoc = pdfDocuments[pdfId];
                
                if (!pdfDoc) {
                    console.warn(`⚠️ PDF document not found for ID: ${pdfId}`);
                    continue;
                }
                
                // CRITICAL: Use pdfId (stable) to get text elements for this specific PDF
                const pdfTextElements = textElements.filter(text => text.pdfId === pdfId);
                const pdfPenStrokes = penStrokes.filter(stroke => stroke.pdfId === pdfId);
                
                console.log(`📄 Processing: ${name} (ID: ${pdfId})`);
                console.log(`   - ${pdfTextElements.length} text elements`);
                console.log(`   - ${pdfPenStrokes.length} pen strokes`);
                
                let file;
                try {
                    // CRITICAL FIX: Create a FRESH copy of the PDF for embedding
                    // This prevents double-embedding from previous exports
                    const originalPdfBytes = await pdfDoc.save();
                    const freshPdfDoc = await PDFDocument.load(originalPdfBytes, { ignoreEncryption: true });
                    const pages = freshPdfDoc.getPages();
                    
                    // Embed font for text rendering
                    let font;
                    try {
                        font = await freshPdfDoc.embedFont(StandardFonts.Helvetica);
                    } catch (e) {
                        console.warn('Could not embed Helvetica, using default');
                    }
                    
                    // Apply text edits to the FRESH PDF copy
                    for (const textElement of pdfTextElements) {
                        const page = pages[textElement.pageIndex];
                        if (page && textElement.text && textElement.text.trim()) {
                            const { height } = page.getSize();
                            
                            // Convert hex color to RGB for pdf-lib
                            const textRgb = hexToRgb(textElement.color || '#000000');
                            
                            // Coordinates are already in PDF space - use directly
                            page.drawText(textElement.text, {
                                x: textElement.x,
                                y: height - textElement.y, // Flip y-axis for PDF coordinates
                                size: textElement.fontSize || 16,
                                color: rgb(textRgb.r, textRgb.g, textRgb.b),
                                font: font
                            });
                        }
                    }
                    
                    // Embed pen strokes as PDF lines
                    for (const stroke of pdfPenStrokes) {
                        const page = pages[stroke.pageIndex];
                        if (page && stroke.points && stroke.points.length > 1) {
                            const { height } = page.getSize();
                            
                            // Convert hex color to RGB for pdf-lib
                            const strokeRgb = hexToRgb(stroke.color || '#000000');
                            
                            // Draw lines connecting all points in the stroke
                            for (let i = 1; i < stroke.points.length; i++) {
                                const startPoint = stroke.points[i - 1];
                                const endPoint = stroke.points[i];
                                
                                page.drawLine({
                                    start: { x: startPoint.x, y: height - startPoint.y },
                                    end: { x: endPoint.x, y: height - endPoint.y },
                                    thickness: stroke.width || 3,
                                    color: rgb(strokeRgb.r, strokeRgb.g, strokeRgb.b),
                                    lineCap: 1, // Round cap
                                });
                            }
                        }
                    }
                    
                    // Save the edited PDF
                    const editedPdfBytes = await freshPdfDoc.save();
                    const blob = new Blob([editedPdfBytes], { type: 'application/pdf' });
                    file = new File([blob], name, { type: 'application/pdf' });
                } catch (pdfLibError) {
                    // pdf-lib can throw on encrypted/quirky PDFs. Use original without edits.
                    console.warn(`⚠️ pdf-lib failed for ${name}, using original PDF without edits:`, pdfLibError?.message || pdfLibError);
                    if (originalFile) {
                        file = originalFile;
                    } else if (url) {
                        const res = await fetch(url);
                        const buf = await res.arrayBuffer();
                        file = new File([buf], name, { type: 'application/pdf' });
                    } else {
                        console.error(`Cannot fallback for ${name}: no original file or url`);
                        continue;
                    }
                }
                
                // Assign to correct category based on type
                if (type === 'cover') {
                    editedPdfs.coverPage = file;
                    console.log('   ✅ Cover page exported');
                } else if (type === 'template') {
                    editedPdfs.template = file;
                    console.log('   ✅ Template exported');
                } else if (type === 'medical') {
                    editedPdfs.medicalRecords.push(file);
                    console.log('   ✅ Medical record exported');
                }
            }

            console.log('✅ All PDFs exported successfully:', {
                hasCoverPage: !!editedPdfs.coverPage,
                hasTemplate: !!editedPdfs.template,
                medicalRecordsCount: editedPdfs.medicalRecords.length
            });
            
            return editedPdfs;
        } catch (error) {
            console.error('❌ Error exporting edited PDFs:', error);
            throw error;
        }
    };

    /**
     * Expose method to get edited PDFs with all annotations applied
     * 
     * CRITICAL FIX: This is the ONLY place where text is embedded into PDFs
     * Uses pdfId (stable identifier) to match text to the correct PDF
     * 
     * This ensures:
     * - Rule 4: Payload contains only the latest version (no duplicates)
     * - Rule 5: Dragging doesn't create duplicates (position was updated in state)
     * - Acceptance: PDF preview matches payload exactly
     */
    React.useImperativeHandle(ref, () => ({
        getEditedPdfs: async () => {
            try {
                console.log('🔄 Exporting edited PDFs with stable identifiers...');
                console.log('   Text elements:', textElements.length);
                console.log('   Pen strokes:', penStrokes.length);
                
                const editedPdfs = {
                    coverPage: null,
                    template: null,
                    medicalRecords: []
                };

                // Process each PDF using its stable pdfId
                for (const pdfInfo of allPdfs) {
                    const { pdfId, type, name, url, file: originalFile } = pdfInfo;
                    const pdfDoc = pdfDocuments[pdfId];
                    
                    if (!pdfDoc) {
                        console.warn(`⚠️ PDF document not found for ID: ${pdfId}`);
                        continue;
                    }
                    
                    // CRITICAL: Use pdfId (stable) to get text elements for this specific PDF
                    const pdfTextElements = textElements.filter(text => text.pdfId === pdfId);
                    const pdfPenStrokes = penStrokes.filter(stroke => stroke.pdfId === pdfId);
                    
                    console.log(`📄 Processing: ${name} (ID: ${pdfId})`);
                    console.log(`   - ${pdfTextElements.length} text elements`);
                    console.log(`   - ${pdfPenStrokes.length} pen strokes`);
                    
                    let file;
                    try {
                        // CRITICAL FIX: Create a FRESH copy of the PDF for embedding
                        // This prevents double-embedding from previous exports
                        const originalPdfBytes = await pdfDoc.save();
                        const freshPdfDoc = await PDFDocument.load(originalPdfBytes, { ignoreEncryption: true });
                        const pages = freshPdfDoc.getPages();
                        
                        // Embed font for text rendering
                        let font;
                        try {
                            font = await freshPdfDoc.embedFont(StandardFonts.Helvetica);
                        } catch (e) {
                            console.warn('Could not embed Helvetica, using default');
                        }
                        
                        // NOTE: Coordinates are already in PDF space because:
                        // - The dataUrl captures canvas at full PDF resolution (pixelRatio=2 * viewport.scale=0.5 = 1x)
                        // - PageCanvas displays at img.width * zoom, coordinates divided by zoom
                        // - So stored coordinates are already in PDF coordinate space
                        
                        // Apply text edits to the FRESH PDF copy
                        // Each text element is embedded ONCE, at its current position
                        for (const textElement of pdfTextElements) {
                            const page = pages[textElement.pageIndex];
                            if (page && textElement.text && textElement.text.trim()) {
                                const { height } = page.getSize();
                                
                                // Convert hex color to RGB for pdf-lib
                                const textRgb = hexToRgb(textElement.color || '#000000');
                                
                                // Coordinates are already in PDF space - use directly
                                // PDF has origin at bottom-left, canvas has origin at top-left
                                page.drawText(textElement.text, {
                                    x: textElement.x,
                                    y: height - textElement.y, // Flip y-axis for PDF coordinates
                                    size: textElement.fontSize || 16,
                                    color: rgb(textRgb.r, textRgb.g, textRgb.b),
                                    font: font
                                });
                                
                                console.log(`   ✓ Embedded text "${textElement.text}" at PDF coords (${textElement.x}, ${height - textElement.y}) on page ${textElement.pageIndex + 1} with color ${textElement.color}`);
                            }
                        }
                        
                        // Embed pen strokes as PDF lines
                        for (const stroke of pdfPenStrokes) {
                            const page = pages[stroke.pageIndex];
                            if (page && stroke.points && stroke.points.length > 1) {
                                const { height } = page.getSize();
                                
                                // Convert hex color to RGB for pdf-lib
                                const strokeRgb = hexToRgb(stroke.color || '#000000');
                                
                                // Draw lines connecting all points in the stroke
                                for (let i = 1; i < stroke.points.length; i++) {
                                    const startPoint = stroke.points[i - 1];
                                    const endPoint = stroke.points[i];
                                    
                                    // Coordinates are already in PDF space - use directly
                                    page.drawLine({
                                        start: { x: startPoint.x, y: height - startPoint.y },
                                        end: { x: endPoint.x, y: height - endPoint.y },
                                        thickness: stroke.width || 3,
                                        color: rgb(strokeRgb.r, strokeRgb.g, strokeRgb.b),
                                        lineCap: 1, // Round cap
                                    });
                                }
                                
                                console.log(`   ✓ Embedded pen stroke with ${stroke.points.length} points on page ${stroke.pageIndex + 1} with color ${stroke.color}`);
                            }
                        }
                        
                        // Save the edited PDF
                        const editedPdfBytes = await freshPdfDoc.save();
                        const blob = new Blob([editedPdfBytes], { type: 'application/pdf' });
                        file = new File([blob], name, { type: 'application/pdf' });
                    } catch (pdfLibError) {
                        // pdf-lib can throw on encrypted/quirky PDFs (save, load, getPages). Use original without edits.
                        console.warn(`⚠️ pdf-lib failed for ${name}, using original PDF without edits:`, pdfLibError?.message || pdfLibError);
                        if (originalFile) {
                            file = originalFile;
                        } else if (url) {
                            const res = await fetch(url);
                            const buf = await res.arrayBuffer();
                            file = new File([buf], name, { type: 'application/pdf' });
                        } else {
                            console.error(`Cannot fallback for ${name}: no original file or url`);
                            continue;
                        }
                    }
                    
                    // Assign to correct category based on type
                    if (type === 'cover') {
                        editedPdfs.coverPage = file;
                        console.log('   ✅ Cover page exported');
                    } else if (type === 'template') {
                        editedPdfs.template = file;
                        console.log('   ✅ Template exported');
                    } else if (type === 'medical') {
                        editedPdfs.medicalRecords.push(file);
                        console.log('   ✅ Medical record exported');
                    }
                }

                console.log('✅ All PDFs exported successfully:', {
                    hasCoverPage: !!editedPdfs.coverPage,
                    hasTemplate: !!editedPdfs.template,
                    medicalRecordsCount: editedPdfs.medicalRecords.length
                });
                
                return editedPdfs;
            } catch (error) {
                console.error('❌ Error exporting edited PDFs:', error);
                throw error;
            }
        }
    }), [pdfDocuments, textElements, penStrokes, allPdfs]);

    if (allPdfs.length === 0) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#f5f5f5",
                    color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                }}
            >
                <PdfIcon sx={{ fontSize: 64, marginBottom: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ opacity: 0.7 }}>
                    No PDF selected
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.5 }}>
                    Select a template or upload files to preview them here
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#f5f5f5",
                    color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                }}
            >
                <PdfIcon sx={{ fontSize: 64, marginBottom: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ opacity: 0.7, marginBottom: 1 }}>
                    Failed to load PDF
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.5 }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    const mainContent = (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
                backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#f5f5f5",
                overflow: "hidden",
            }}
        >
            {/* Header with controls */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    borderBottom: `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}`,
                    backgroundColor: themeMode === 'dark' ? "#252144" : "#ffffff",
                    flexShrink: 0,
                }}
            >
                {/* File info */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <PdfIcon sx={{ fontSize: 20, color: themeMode === 'dark' ? "#8B8FA8" : "#666666" }} />
                    
                    {/* Page count */}
                    <Typography
                        variant="body2"
                        sx={{
                            color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                            fontSize: "12px",
                            padding: "4px 12px",
                            backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#f5f5f5",
                            borderRadius: "4px",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {pageImages.length} {pageImages.length === 1 ? 'Page' : 'Pages'} Loaded
                    </Typography>
                </Box>

                {/* Editing and zoom controls */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {/* Editing controls */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 1.5 }}>
                        <Tooltip title={isEditing ? "Exit Edit Mode" : "Enter Edit Mode"}>
                            <IconButton
                                onClick={handleEditToggle}
                                size="small"
                                sx={{
                                    color: isEditing ? "#FF6B47" : (themeMode === 'dark' ? "#8B8FA8" : "#666666"),
                                    "&:hover": {
                                        backgroundColor: themeMode === 'dark' ? "rgba(255, 107, 71, 0.1)" : "rgba(255, 107, 71, 0.05)",
                                    },
                                }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        {isEditing && (
                            <>
                                <Tooltip title="Add Text (Double-click)">
                                    <IconButton
                                        onClick={() => handleEditModeChange('text')}
                                        size="small"
                                        sx={{
                                            color: editMode === 'text' ? "#FF6B47" : (themeMode === 'dark' ? "#8B8FA8" : "#666666"),
                                            "&:hover": {
                                                backgroundColor: themeMode === 'dark' ? "rgba(255, 107, 71, 0.1)" : "rgba(255, 107, 71, 0.05)",
                                            },
                                        }}
                                    >
                                        <TextIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                {/* Text Color Picker */}
                                <Tooltip title="Text Color">
                                    <IconButton
                                        onClick={(e) => setTextColorAnchor(e.currentTarget)}
                                        size="small"
                                        sx={{
                                            color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                            "&:hover": {
                                                backgroundColor: themeMode === 'dark' ? "rgba(255, 107, 71, 0.1)" : "rgba(255, 107, 71, 0.05)",
                                            },
                                        }}
                                    >
                                        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <TextIcon fontSize="small" />
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: -2,
                                                    left: 0,
                                                    right: 0,
                                                    height: '3px',
                                                    backgroundColor: textColor,
                                                    borderRadius: '1px',
                                                }}
                                            />
                                        </Box>
                                    </IconButton>
                                </Tooltip>
                                <ColorPicker
                                    color={textColor}
                                    onChange={setTextColor}
                                    label="Text Color"
                                    themeMode={themeMode}
                                    anchorEl={textColorAnchor}
                                    onClose={() => setTextColorAnchor(null)}
                                />

                                <Tooltip title="Draw with Pen">
                                    <IconButton
                                        onClick={() => handleEditModeChange('pen')}
                                        size="small"
                                        sx={{
                                            color: editMode === 'pen' ? "#FF6B47" : (themeMode === 'dark' ? "#8B8FA8" : "#666666"),
                                            "&:hover": {
                                                backgroundColor: themeMode === 'dark' ? "rgba(255, 107, 71, 0.1)" : "rgba(255, 107, 71, 0.05)",
                                            },
                                        }}
                                    >
                                        <PenIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                {/* Pen Color Picker */}
                                <Tooltip title="Pen Color">
                                    <IconButton
                                        onClick={(e) => setPenColorAnchor(e.currentTarget)}
                                        size="small"
                                        sx={{
                                            color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                            "&:hover": {
                                                backgroundColor: themeMode === 'dark' ? "rgba(255, 107, 71, 0.1)" : "rgba(255, 107, 71, 0.05)",
                                            },
                                        }}
                                    >
                                        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <PenIcon fontSize="small" />
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    bottom: -2,
                                                    left: 0,
                                                    right: 0,
                                                    height: '3px',
                                                    backgroundColor: penColor,
                                                    borderRadius: '1px',
                                                }}
                                            />
                                        </Box>
                                    </IconButton>
                                </Tooltip>
                                <ColorPicker
                                    color={penColor}
                                    onChange={setPenColor}
                                    label="Pen Color"
                                    themeMode={themeMode}
                                    anchorEl={penColorAnchor}
                                    onClose={() => setPenColorAnchor(null)}
                                />

                                {/* Font Size Selector */}
                                <Tooltip
                                    title="Font Size"
                                    open={fontSizeSelectOpen ? false : undefined}
                                    disableHoverListener={fontSizeSelectOpen}
                                    disableFocusListener={fontSizeSelectOpen}
                                    disableTouchListener={fontSizeSelectOpen}
                                    PopperProps={{
                                        style: { display: fontSizeSelectOpen ? 'none' : 'block' }
                                    }}
                                >
                                    <Box component="span" sx={{ display: 'inline-block' }}>
                                        <Select
                                            size="small"
                                            value={textFontSize}
                                            onChange={(e) => setTextFontSize(Number(e.target.value))}
                                            onOpen={() => setFontSizeSelectOpen(true)}
                                            onClose={() => setFontSizeSelectOpen(false)}
                                            sx={{
                                                minWidth: 56,
                                                height: 32,
                                                fontSize: 12,
                                                color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                            }}
                                        >
                                            {[8, 10, 12, 14, 16].map((n) => (
                                                <MenuItem key={n} value={n} sx={{ fontSize: 10 }}>{n}</MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                </Tooltip>

                                {/* Pen Thickness Selector */}
                                <Tooltip
                                    title="Pen Thickness"
                                    open={penThicknessSelectOpen ? false : undefined}
                                    disableHoverListener={penThicknessSelectOpen}
                                    disableFocusListener={penThicknessSelectOpen}
                                    disableTouchListener={penThicknessSelectOpen}
                                    PopperProps={{
                                        style: { display: penThicknessSelectOpen ? 'none' : 'block' }
                                    }}
                                >
                                    <Box component="span" sx={{ display: 'inline-block' }}>
                                        <Select
                                            size="small"
                                            value={penWidth}
                                            onChange={(e) => setPenWidth(Number(e.target.value))}
                                            onOpen={() => setPenThicknessSelectOpen(true)}
                                            onClose={() => setPenThicknessSelectOpen(false)}
                                            sx={{
                                                minWidth: 48,
                                                height: 32,
                                                fontSize: 12,
                                                color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                            }}
                                        >
                                            {[1, 2, 3, 4].map((n) => (
                                                <MenuItem key={n} value={n} sx={{ fontSize: 12 }}>{n}px</MenuItem>
                                            ))}
                                        </Select>
                                    </Box>
                                </Tooltip>

                                {/* Eraser Tool */}
                                <Tooltip title="Eraser (Erase pen strokes)">
                                    <IconButton
                                        onClick={() => handleEditModeChange('eraser')}
                                        size="small"
                                        sx={{
                                            color: editMode === 'eraser' ? "#FF6B47" : (themeMode === 'dark' ? "#8B8FA8" : "#666666"),
                                            "&:hover": {
                                                backgroundColor: themeMode === 'dark' ? "rgba(255, 107, 71, 0.1)" : "rgba(255, 107, 71, 0.05)",
                                            },
                                        }}
                                    >
                                        <EraserIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Delete Selected">
                                    <IconButton
                                        onClick={handleDeleteText}
                                        disabled={!selectedTextElement}
                                        size="small"
                                        sx={{
                                            color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                            "&:hover": {
                                                backgroundColor: themeMode === 'dark' ? "rgba(255, 107, 71, 0.1)" : "rgba(255, 107, 71, 0.05)",
                                            },
                                            "&:disabled": {
                                                color: themeMode === 'dark' ? "#4a4867" : "#b0b0b0",
                                            },
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Download Edited PDF">
                                    <IconButton
                                        onClick={handleSavePdf}
                                        size="small"
                                        sx={{
                                            color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                            "&:hover": {
                                                backgroundColor: themeMode === 'dark' ? "rgba(255, 107, 71, 0.1)" : "rgba(255, 107, 71, 0.05)",
                                            },
                                        }}
                                    >
                                        <SaveIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </>
                        )}
                    </Box>

                    {/* Zoom controls */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <IconButton
                            onClick={handleZoomOut}
                            disabled={currentZoomIndex === 0}
                            size="small"
                            sx={{
                                color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                "&:hover": {
                                    backgroundColor: themeMode === 'dark' ? "rgba(255, 107, 71, 0.1)" : "rgba(255, 107, 71, 0.05)",
                                },
                                "&:disabled": {
                                    color: themeMode === 'dark' ? "#4a4867" : "#b0b0b0",
                                },
                            }}
                        >
                            <ZoomOutIcon fontSize="small" />
                        </IconButton>

                        <Typography
                            variant="body2"
                            sx={{
                                color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                fontSize: "12px",
                                minWidth: "40px",
                                textAlign: "center",
                            }}
                        >
                            {Math.round(zoom * 100)}%
                        </Typography>

                        <Button
                            onClick={handleResetZoom}
                            size="small"
                            sx={{
                                minWidth: "auto",
                                padding: "4px 12px",
                                fontSize: "12px",
                                color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                backgroundColor: "transparent",
                                border: `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}`,
                                borderRadius: "4px",
                                textTransform: "uppercase",
                                "&:hover": {
                                    backgroundColor: themeMode === 'dark' ? "#1B1A33" : "#f5f5f5",
                                    borderColor: themeMode === 'dark' ? "#4a4867" : "#ccc",
                                },
                            }}
                            variant="outlined"
                        >
                            Reset
                        </Button>

                        <IconButton
                            onClick={handleZoomIn}
                            disabled={currentZoomIndex === zoomLevels.length - 1}
                            size="small"
                            sx={{
                                color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                "&:hover": {
                                    backgroundColor: themeMode === 'dark' ? "rgba(255, 107, 71, 0.1)" : "rgba(255, 107, 71, 0.05)",
                                },
                                "&:disabled": {
                                    color: themeMode === 'dark' ? "#4a4867" : "#b0b0b0",
                                },
                            }}
                        >
                            <ZoomInIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            {/* PDF Canvas - All Pages Vertically */}
            <Box
                ref={containerRef}
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    padding: "28px",
                    overflow: "auto",
                    position: "relative",
                    gap: "24px",
                }}
            >
                {loading ? (
                    <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                        <img
                            style={{ width: '100px', height: '100px' }}
                            src={themeMode === "dark" ? "/icons/loader_white.gif" : "/icons/loader_black.gif"}
                            alt="Loading"
                        />
                    </div>
                ) : pageImages.length > 0 ? (
                    pageImages.map((pageImage, index) => {
                        // Group pages by PDF for labeling
                        const isFirstPageOfPDF = index === 0 || pageImages[index - 1]?.pdfIndex !== pageImage.pdfIndex;
                        
                        return (
                            <div key={`page-${pageImage.pdfIndex}-${pageImage.pageIndex}`} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                {/* PDF Name Label */}
                                {isFirstPageOfPDF && (
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            fontWeight: 600,
                                            color: themeMode === 'dark' ? "#8B8FA8" : "#666666",
                                            marginBottom: "10px",
                                            padding: "8px 16px",
                                            backgroundColor: themeMode === 'dark' ? "#252144" : "#f5f5f5",
                                            borderRadius: "8px",
                                            border: `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}`,
                                        }}
                                    >
                                        📄 {pageImage.pdfName || `Document ${pageImage.pdfIndex + 1}`}
                                    </Typography>
                                )}
                                
                                <PageCanvas
                                    pageImage={pageImage}
                                    zoom={zoom}
                                    themeMode={themeMode}
                                    isEditing={isEditing}
                                    editMode={editMode}
                                    isDragging={isDragging}
                                    hoveredText={hoveredText}
                                    textElements={textElements}
                                    penStrokes={penStrokes}
                                    editingText={editingText}
                                    editingValue={editingValue}
                                    selectedTextElement={selectedTextElement}
                                    onCanvasClick={handleCanvasClick}
                                    onCanvasDoubleClick={handleCanvasDoubleClick}
                                    onCanvasMouseDown={handleCanvasMouseDown}
                                    onCanvasMouseMove={handleCanvasHover}
                                    onTextInputChange={handleTextInputChange}
                                    onFinishTextEdit={handleFinishTextEdit}
                                    inputRef={inputRef}
                                    renderPenStrokes={renderPenStrokes}
                                    renderTextElements={renderTextElements}
                                />
                            </div>
                        );
                    })
                ) : null}
                
                {/* Error message */}
                {error && (
                    <Box
                        sx={{
                            padding: "12px 24px",
                            backgroundColor: themeMode === 'dark' ? "#2d1b1b" : "#ffeaea",
                            border: `1px solid ${themeMode === 'dark' ? "#8b0000" : "#ff4d4f"}`,
                            borderRadius: "8px",
                            color: themeMode === 'dark' ? "#ff6b6b" : "#d32f2f",
                            fontSize: "14px",
                            maxWidth: "600px",
                        }}
                    >
                        ⚠️ {error}
                    </Box>
                )}
            </Box>

            {/* Editing mode indicator */}
            {isEditing && (
                <Box
                    sx={{
                        padding: "8px 16px",
                        backgroundColor: themeMode === 'dark' ? "#04002b" : "#f5f5f5",
                        color: themeMode === 'dark' ? "#ffffff" : "#000000",
                        textAlign: "center",
                        fontSize: "12px",
                        fontWeight: 500,
                        border: `1px solid ${themeMode === 'dark' ? "#302d4c" : "#e0e0e0"}`,
                        borderLeft: "none",
                        borderRight: "none",
                    }}
                >
                    Edit Mode: {editMode === 'text' ? 'Add Text (Double-click to add new | Single-click to select | Double-click selected to edit | Drag to move)' : editMode === 'pen' ? 'Draw (Click and drag to draw)' : editMode === 'eraser' ? 'Eraser (Click and drag over pen strokes to erase)' : 'Select'} | Changes saved automatically
                </Box>
            )}
        </Box>
    );

    // Return main content (fullscreen is now handled by parent component)
    return mainContent;
});

export default PdfViewer;
